import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Users, Eye, MousePointerClick, BarChart3, Calendar, Download, RefreshCw, Loader2 } from 'lucide-react';
import { getAnalyticsSummary, getRealtimeAnalytics, getConversionRate } from '../firebase/analytics';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';

export function AdminAnalyticsTab({ bookings, products, orders }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('7d'); // 7d, 30d, 90d, all
  const [conversionRate, setConversionRate] = useState(0);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const endDate = new Date();
      let startDate;
      
      switch (dateRange) {
        case '7d':
          startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(endDate.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(0); // All time
      }

      const [summary, rate] = await Promise.all([
        getAnalyticsSummary(startDate, endDate),
        getConversionRate(startDate, endDate)
      ]);

      setAnalytics(summary);
      setConversionRate(rate);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateOrderStats = () => {
    const totalRevenue = orders
      .filter(o => o.paymentStatus === 'completed')
      .reduce((sum, o) => sum + (o.total || 0), 0);
    
    const completedOrders = orders.filter(o => o.paymentStatus === 'completed').length;
    const pendingOrders = orders.filter(o => o.paymentStatus === 'pending').length;
    const averageOrderValue = completedOrders > 0 ? totalRevenue / completedOrders : 0;

    return {
      totalRevenue,
      completedOrders,
      pendingOrders,
      averageOrderValue
    };
  };

  const orderStats = calculateOrderStats();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-[#c5a059]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Analytics Dashboard</h2>
          <HelpTooltip
            title="Analytics Dashboard"
            content="Track your business performance with comprehensive analytics. Monitor revenue, page views, email signups, conversion rates, and more. Use date ranges to analyze different time periods. See which products perform best and identify growth opportunities."
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>
          <button
            onClick={loadAnalytics}
            className="flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition"
          >
            <RefreshCw size={18} className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      <FeatureGuide
        title="Analytics Guide"
        description="Analytics help you understand how your business is performing. Track key metrics like revenue, conversions, page views, and customer behavior. Use this data to make informed decisions about products, funnels, and marketing."
        steps={[
          "Select a date range to analyze (7 days, 30 days, 90 days, or all time)",
          "View key metrics: Revenue, Page Views, Email Signups, Conversion Rate",
          "Check order statistics: Total orders, completed orders, average order value",
          "Review booking statistics: Total bookings, confirmed, pending, cancelled",
          "See top pages and products to identify what's working",
          "Click 'Refresh' to update data in real-time",
          "Use insights to optimize your funnels and products"
        ]}
        tips={[
          "Check analytics weekly to track trends",
          "Conversion rate shows how many visitors become customers",
          "Average order value helps you price products effectively",
          "Top products show what your audience wants most",
          "Compare different date ranges to see growth",
          "Low conversion rates may indicate pricing or messaging issues"
        ]}
      />

      <InfoBanner type="info" title="Understanding Metrics">
        <ul className="list-disc list-inside space-y-1 text-sm">
          <li><strong>Revenue:</strong> Total money from completed orders in the selected period</li>
          <li><strong>Page Views:</strong> Number of times pages were viewed (unique sessions tracked separately)</li>
          <li><strong>Email Signups:</strong> New subscribers added to your email list</li>
          <li><strong>Conversion Rate:</strong> Percentage of page visitors who make a purchase</li>
          <li><strong>Average Order Value:</strong> Average amount spent per order</li>
        </ul>
      </InfoBanner>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="text-green-600" size={24} />
            <span className="text-xs text-green-600 font-medium">Revenue</span>
          </div>
          <p className="text-3xl font-bold text-green-800">£{orderStats.totalRevenue.toFixed(2)}</p>
          <p className="text-sm text-green-600 mt-1">{orderStats.completedOrders} orders</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
          <div className="flex items-center justify-between mb-4">
            <Eye className="text-blue-600" size={24} />
            <span className="text-xs text-blue-600 font-medium">Page Views</span>
          </div>
          <p className="text-3xl font-bold text-blue-800">{analytics?.pageViews || 0}</p>
          <p className="text-sm text-blue-600 mt-1">{analytics?.uniqueSessions || 0} unique sessions</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-purple-600" size={24} />
            <span className="text-xs text-purple-600 font-medium">Email Signups</span>
          </div>
          <p className="text-3xl font-bold text-purple-800">{analytics?.emailSignups || 0}</p>
          <p className="text-sm text-purple-600 mt-1">New subscribers</p>
        </div>

        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl border border-yellow-200">
          <div className="flex items-center justify-between mb-4">
            <TrendingUp className="text-yellow-600" size={24} />
            <span className="text-xs text-yellow-600 font-medium">Conversion Rate</span>
          </div>
          <p className="text-3xl font-bold text-yellow-800">{conversionRate.toFixed(1)}%</p>
          <p className="text-sm text-yellow-600 mt-1">Purchase rate</p>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Orders Overview */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Orders Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Orders</span>
              <span className="font-semibold text-gray-800">{orders.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Completed</span>
              <span className="font-semibold text-green-600">{orderStats.completedOrders}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending</span>
              <span className="font-semibold text-yellow-600">{orderStats.pendingOrders}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Average Order Value</span>
              <span className="font-semibold text-gray-800">£{orderStats.averageOrderValue.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Bookings Overview */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Bookings Overview</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Bookings</span>
              <span className="font-semibold text-gray-800">{bookings.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Confirmed</span>
              <span className="font-semibold text-green-600">
                {bookings.filter(b => b.status === 'confirmed').length}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Pending</span>
              <span className="font-semibold text-yellow-600">
                {bookings.filter(b => b.status === 'pending').length}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Cancelled</span>
              <span className="font-semibold text-red-600">
                {bookings.filter(b => b.status === 'cancelled').length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Top Pages */}
      {analytics?.topPages && Object.keys(analytics.topPages).length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Pages</h3>
          <div className="space-y-3">
            {Object.entries(analytics.topPages)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([page, views]) => (
                <div key={page} className="flex items-center justify-between">
                  <span className="text-gray-700">{page}</span>
                  <div className="flex items-center gap-3">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#c5a059] h-2 rounded-full"
                        style={{ width: `${(views / analytics.pageViews) * 100}%` }}
                      />
                    </div>
                    <span className="font-semibold text-gray-800 w-12 text-right">{views}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Top Products */}
      {analytics?.topProducts && Object.keys(analytics.topProducts).length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Products</h3>
          <div className="space-y-3">
            {Object.entries(analytics.topProducts)
              .sort((a, b) => b[1] - a[1])
              .slice(0, 5)
              .map(([productId, sales]) => {
                const product = products.find(p => p.id === productId);
                return (
                  <div key={productId} className="flex items-center justify-between">
                    <span className="text-gray-700">{product?.title || productId}</span>
                    <span className="font-semibold text-gray-800">{sales} sales</span>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

